import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';

import { CatalogDefinition, CatalogOption, ClosureSearchGrammar, FlowDefinition, FlowNode } from '../../grammar/closure-search.grammar';

@Component({
    selector: 'lib-grammar-renderer',
    templateUrl: './grammar-renderer.component.html',
    styleUrls: ['./grammar-renderer.component.scss'],
    standalone: false,
})
export class GrammarRendererComponent implements OnInit, OnChanges, OnDestroy {
    @Input() grammar: ClosureSearchGrammar;
    @Input() flowId = 'setup';
    @Input() context: Record<string, any> = {};

    public form: FormGroup;
    public flow: FlowDefinition | null = null;
    public contextValues: Record<string, any> = {};
    public resolvedContext: Record<string, any> = {};
    private readonly fallbackControl = new FormControl(undefined);
    private nodePersistMap: Record<string, string> = {};
    private conditionalValidatorsSubscription?: Subscription;
    private monthOptions: CatalogOption[] = [
        { label: 'January', value: 'JANUARY' },
        { label: 'February', value: 'FEBRUARY' },
        { label: 'March', value: 'MARCH' },
        { label: 'April', value: 'APRIL' },
        { label: 'May', value: 'MAY' },
        { label: 'June', value: 'JUNE' },
        { label: 'July', value: 'JULY' },
        { label: 'August', value: 'AUGUST' },
        { label: 'September', value: 'SEPTEMBER' },
        { label: 'October', value: 'OCTOBER' },
        { label: 'November', value: 'NOVEMBER' },
        { label: 'December', value: 'DECEMBER' },
    ];

    constructor(private fb: FormBuilder) {
        this.form = this.fb.group({});
    }

    ngOnInit(): void {
        this.initializeForm();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.grammar || changes.flowId) {
            this.initializeForm();
        }
        if (changes.context) {
            this.resolveContext();
            this.applyInitialFormValuesFromContext();
            this.applyConditionalRequiredValidators();
        }
    }

    private initializeForm(): void {
        if (!this.grammar) return;

        this.conditionalValidatorsSubscription?.unsubscribe();
        this.flow = this.grammar.flows.find(f => f.id === this.flowId) || null;
        this.form = this.fb.group({});
        this.nodePersistMap = {};
        if (this.flow?.sections?.length > 0) {
            this.flow.sections.forEach(section => this.registerNodes(section.nodes || []));
        }
        this.resolveContext();
        this.applyInitialFormValuesFromContext();
        this.applyConditionalRequiredValidators();
        this.conditionalValidatorsSubscription = this.form.valueChanges.subscribe(() => this.applyConditionalRequiredValidators());
    }

    ngOnDestroy(): void {
        this.conditionalValidatorsSubscription?.unsubscribe();
    }

    /**
     * A `required: true` node only enforces `Validators.required` while it is actually rendered/visible
     * - i.e. its own `visibleWhen` passes AND every ancestor group's `visibleWhen` passes too (mirrors the
     * template's nested `*ngIf="isVisible(...)"` gating). E.g. the 13-period "Date Field" is mandatory only
     * while `pattern.mode === 'THIRTEEN_PERIOD'` (its parent group's visibility), and must not block saving
     * a STANDARD mode configuration. Re-evaluated on every form change since visibility can depend on any
     * other control's value.
     */
    private applyConditionalRequiredValidators(): void {
        this.getAllNodes()
            .filter(node => this.isControlNode(node) && node.required)
            .forEach(node => {
                const control = this.form.get([this.getControlKey(node)]);
                if (!control) return;
                control.setValidators(this.isEffectivelyVisible(node) ? [Validators.required] : []);
                control.updateValueAndValidity({ emitEvent: false });
            });
    }

    private isEffectivelyVisible(node: FlowNode): boolean {
        if (!this.isVisible(node)) return false;
        const parent = this.findParentNode(node);
        return parent ? this.isEffectivelyVisible(parent) : true;
    }

    private findParentNode(target: FlowNode): FlowNode | null {
        let result: FlowNode | null = null;
        const search = (nodes: FlowNode[], parent: FlowNode | null) => {
            for (const node of nodes) {
                if (result) return;
                if (node === target) {
                    result = parent;
                    return;
                }
                if (node.kind === 'group' && node.nodes?.length) {
                    search(node.nodes, node);
                }
            }
        };
        this.flow?.sections?.forEach(section => search(section.nodes || [], null));
        return result;
    }

    private registerNodes(nodes: FlowNode[]): void {
        nodes.forEach(node => {
            if (this.isControlNode(node)) {
                const controlKey = this.getControlKey(node);
                this.nodePersistMap[node.id] = controlKey;
                if (!this.form.contains(controlKey)) {
                    const initialValue = node.kind === 'select' ? '' : undefined;
                    const control = new FormControl(initialValue);
                    if (node.disabled) {
                        control.disable();
                    }
                    control.setValidators(node.required ? [Validators.required] : []);
                    this.form.addControl(controlKey, control);
                } else {
                    const control = this.form.get([controlKey]);
                    if (control && node.disabled && control.enabled) {
                        control.disable();
                    } else if (control && !node.disabled && control.disabled) {
                        control.enable();
                    }
                    control.setValidators(node.required ? [Validators.required] : []);
                    this.form.addControl(controlKey, control);
                }
            }
            if (node.kind === 'group' && node.nodes?.length) {
                this.registerNodes(node.nodes);
            }
        });
    }

    private applyInitialFormValuesFromContext(): void {
        if (!this.form || !this.context) {
            return;
        }

        const initialValues: Record<string, any> = {};
        Object.keys(this.form.controls).forEach(controlKey => {
            const directValue = this.context[controlKey];
            const nestedValue = this.getNestedContextValue(controlKey);
            const value = directValue !== undefined ? directValue : nestedValue;

            if (value !== undefined) {
                initialValues[controlKey] = value;
            }
        });

        if (Object.keys(initialValues).length > 0) {
            this.form.patchValue(initialValues, { emitEvent: false });
        }

        // Auto-populate datatype fields for selected fields that don't have datatype values
        this.populateDatatypeFieldsFromContext();
        // Auto-populate format fields (Year/Month/Quarter Format) from grammar-declared derivation rules
        this.populateFormatFieldsFromContext();
    }

    private populateDatatypeFieldsFromContext(): void {
        const fieldMappings = [
            { fieldControl: 'mapping.standard.yearField', datatypeControl: 'mapping.standard.yearFieldDatatype' },
            { fieldControl: 'mapping.standard.monthField', datatypeControl: 'mapping.standard.monthFieldDatatype' },
            { fieldControl: 'mapping.standard.quarterField', datatypeControl: 'mapping.standard.quarterFieldDatatype' },
        ];

        fieldMappings.forEach(mapping => {
            const selectedFieldValue = this.form.get([mapping.fieldControl])?.value;
            if (!selectedFieldValue) return;

            const existingDatatypeValue = this.form.get([mapping.datatypeControl])?.value;
            if (existingDatatypeValue) return;

            // Default to 'STRING' datatype for backward compatibility with existing saved data
            // This ensures format fields are visible even when datatype wasn't persisted
            this.form.get([mapping.datatypeControl])?.setValue('STRING');
        });
    }

    /**
     * On initial load / hydrate, auto-derive Year/Month/Quarter Format for every field-select node that
     * declares a `formatDerivation` rule in the grammar, but only when the target format control is still
     * empty (i.e. never overwrite a persisted/user-selected format).
     */
    private populateFormatFieldsFromContext(): void {
        this.getAllNodes()
            .filter(node => !!node.formatDerivation)
            .forEach(fieldNode => {
                const selectedValue = this.form.get([this.getControlKey(fieldNode)])?.value;
                if (selectedValue) {
                    this.applyFormatDerivation(fieldNode, selectedValue, false);
                }
            });
    }

    /**
     * Derives the target format control's value from the selected field's tag metadata, following the
     * `formatDerivation` rule declared on `fieldNode` in the grammar. Set `overwriteExisting` to `true`
     * when reacting to a live field selection change (user just picked a new field), or `false` when
     * hydrating from persisted context (never clobber an existing/user-selected format).
     */
    private applyFormatDerivation(fieldNode: FlowNode, selectedValue: string, overwriteExisting: boolean): void {
        const rule = fieldNode?.formatDerivation;
        if (!rule || !selectedValue) return;

        const targetNode = this.findNodeById(rule.targetNodeId);
        if (!targetNode) return;

        if (!overwriteExisting) {
            const existingValue = this.form.get([this.getControlKey(targetNode)])?.value;
            if (existingValue) return;
        }

        const fieldOptions = this.getNodeOptions(fieldNode);
        const selectedOption = fieldOptions.find(opt => opt.value === selectedValue);
        const tagEntries = selectedOption?.tagEntries;
        if (!tagEntries?.length) return;

        const targetOptions = this.getNodeOptions(targetNode);
        const resolvedFormat = this.resolveFormatFromTags(tagEntries, rule.tagKeyPriority, rule.valueMap || {}, targetOptions);
        if (resolvedFormat) {
            this.onNodeValueChange(targetNode, resolvedFormat);
        }
    }

    /**
     * Resolves a target catalog value from a field's tag entries: an explicit `format` tag is checked first
     * (its value is expected to match a target catalog option value directly), followed by the legacy
     * `gst-filing-field` tag (its value is translated via `valueMap`). Returns `undefined` if no tag matches.
     */
    private resolveFormatFromTags(
        tagEntries: { key: string; value: string }[],
        tagKeyPriority: string[],
        valueMap: Record<string, string>,
        targetCatalogOptions: CatalogOption[]
    ): string | undefined {
        const validTargetValues = new Set(targetCatalogOptions.map(option => option.value));

        for (const tagKey of tagKeyPriority) {
            const tag = tagEntries.find(entry => entry.key === tagKey);
            if (!tag?.value) continue;

            if (tagKey === 'format') {
                // Match case-insensitively but return the catalog's own casing (e.g. quarter format 'Q' is uppercase).
                const matchedOption = targetCatalogOptions.find(option => option.value.toLowerCase() === tag.value.toLowerCase());
                if (matchedOption) {
                    return matchedOption.value;
                }
                continue;
            }

            const mappedValue = valueMap[tag.value];
            if (mappedValue && validTargetValues.has(mappedValue)) {
                return mappedValue;
            }
        }

        return undefined;
    }

    private getAllNodes(): FlowNode[] {
        const result: FlowNode[] = [];
        const traverse = (nodes: FlowNode[]) => {
            nodes.forEach(node => {
                result.push(node);
                if (node.kind === 'group' && node.nodes?.length) {
                    traverse(node.nodes);
                }
            });
        };
        this.flow?.sections?.forEach(section => traverse(section.nodes || []));
        return result;
    }

    private getNestedContextValue(key: string): any {
        const keyParts = key.split('.');
        let currentValue: any = this.context;

        for (const part of keyParts) {
            if (currentValue == null || typeof currentValue !== 'object' || !(part in currentValue)) {
                return undefined;
            }

            currentValue = currentValue[part];
        }

        return currentValue;
    }

    private isControlNode(node: FlowNode): boolean {
        return node.kind === 'select' || node.kind === 'text';
    }

    public getControlKey(node: FlowNode): string {
        return node.persistAs || node.id;
    }

    private resolveContext(): void {
        if (!this.grammar || !this.grammar.contextBindings) return;

        this.resolvedContext = {};
        this.grammar.contextBindings.forEach(binding => {
            const plainRef = binding.ref.replace(/^context\./, '');
            const value = this.context[binding.ref] ?? this.context[plainRef];
            if (value !== undefined) {
                this.resolvedContext[binding.ref] = value;
                this.resolvedContext[plainRef] = value;
            }
        });
    }

    public getCatalog(catalogId: string): CatalogDefinition | null {
        return this.grammar?.catalogs.find(c => c.id === catalogId) || null;
    }

    private getControl(controlKey: string): FormControl | null {
        if (!controlKey) {
            return null;
        }

        return this.form.get([controlKey]) as FormControl | null;
    }

    public getFormControl(persistAs: string): FormControl {
        if (!persistAs) {
            return this.fallbackControl;
        }

        let control = this.getControl(persistAs);
        if (control) {
            return control;
        }

        this.form.addControl(persistAs, new FormControl(undefined));
        control = this.getControl(persistAs);
        return control || this.fallbackControl;
    }

    public getNodeOptions(node: FlowNode): CatalogOption[] {
        const catalogOptions = node.catalogId ? this.getCatalog(node.catalogId)?.options || [] : [];
        if (catalogOptions.length > 0) {
            return catalogOptions;
        }

        const isMonthSelect =
            node.kind === 'select' &&
            ((node.label || '').toLowerCase().includes('month') || (node.persistAs || '').toLowerCase().includes('month'));

        if (isMonthSelect) {
            return this.monthOptions;
        }

        return [];
    }

    public isVisible(node: FlowNode): boolean {
        if (!node.visibleWhen) return true;

        const { all, any } = node.visibleWhen;

        if (all && all.length > 0) {
            return all.every(rule => this.evaluateRule(rule));
        }

        if (any && any.length > 0) {
            return any.some(rule => this.evaluateRule(rule));
        }

        return true;
    }

    private evaluateRule(rule: { ref: string; operator: 'eq' | 'neq'; value: string | boolean }): boolean {
        const actualValue = this.getValue(rule.ref);
        const expectedValue = rule.value;

        if (rule.operator === 'eq') {
            return actualValue === expectedValue;
        } else if (rule.operator === 'neq') {
            return actualValue !== expectedValue;
        }

        return false;
    }

    private getValue(ref: string): any {
        const mappedPersistAs = this.nodePersistMap[ref];
        if (mappedPersistAs) {
            const mappedValue = this.getControl(mappedPersistAs)?.value;
            if (mappedValue !== undefined) {
                return mappedValue;
            }
        }

        const directControlValue = this.getControl(ref)?.value;
        if (directControlValue !== undefined) {
            return directControlValue;
        }

        // Check form values first
        if (this.form.value[ref] !== undefined) {
            return this.form.value[ref];
        }

        // Check resolved context
        if (this.resolvedContext[ref] !== undefined) {
            return this.resolvedContext[ref];
        }

        // Check context values
        if (this.contextValues[ref] !== undefined) {
            return this.contextValues[ref];
        }

        return undefined;
    }

    public onNodeValueChange(node: FlowNode, value: any): void {
        if (node.persistAs) {
            const control = this.form.get([node.persistAs]);
            const wasDisabled = control?.disabled;
            if (wasDisabled) {
                control.enable();
            }
            this.form.patchValue({ [node.persistAs]: value });
            if (wasDisabled) {
                control.disable();
            }
            this.contextValues[node.persistAs] = value;
        }
        this.contextValues[node.id] = value;
    }

    public onInputChange(node: FlowNode, event: Event): void {
        const value = (event.target as HTMLInputElement).value;
        this.onNodeValueChange(node, value);
    }

    public onSelectChange(node: FlowNode, event: Event): void {
        const value = (event.target as HTMLSelectElement).value;
        this.onNodeValueChange(node, value);

        // Auto-populate datatype field when a field is selected
        if (
            node.id === 'setup.mapping.standard.yearField' ||
            node.id === 'setup.mapping.standard.monthField' ||
            node.id === 'setup.mapping.standard.quarterField'
        ) {
            const catalogOptions = this.getNodeOptions(node);
            const selectedOption = catalogOptions.find(opt => opt.value === value);
            if (selectedOption && (selectedOption as any).datatype) {
                const datatypeFieldId = node.id + 'Datatype';
                const datatypeNode = this.findNodeById(datatypeFieldId);
                if (datatypeNode) {
                    this.onNodeValueChange(datatypeNode, (selectedOption as any).datatype);
                }
            }
        }

        // Auto-populate the corresponding format field (Year/Month/Quarter Format) using the grammar-declared
        // derivation rule, overwriting any previous auto-derived/format value since the field just changed.
        if (node.formatDerivation) {
            this.applyFormatDerivation(node, value, true);
        }
    }

    private findNodeById(nodeId: string): FlowNode | null {
        if (!this.flow?.sections) return null;
        for (const section of this.flow.sections) {
            const found = this.findNodeInNodes(section.nodes || [], nodeId);
            if (found) return found;
        }
        return null;
    }

    private findNodeInNodes(nodes: FlowNode[], nodeId: string): FlowNode | null {
        for (const node of nodes) {
            if (node.id === nodeId) return node;
            if (node.kind === 'group' && node.nodes) {
                const found = this.findNodeInNodes(node.nodes, nodeId);
                if (found) return found;
            }
        }
        return null;
    }

    public renderPreview(template: string, dependsOn: string[]): string {
        let rendered = template;
        dependsOn.forEach(ref => {
            const value = this.getValue(ref);
            const placeholder = `{{${ref}}}`;
            rendered = rendered.replace(new RegExp(placeholder, 'g'), this.getDummyValue(value, ref));
        });
        return rendered;
    }

    private getDummyValue(value: any, ref: string): string {
        if (value !== undefined) {
            return String(value);
        }

        // Return dummy values based on the reference key
        if (ref.includes('yearField')) return 'entity.year';
        if (ref.includes('monthField')) return 'entity.month';
        if (ref.includes('quarterField')) return 'entity.quarter';
        if (ref.includes('dateFields')) return 'fromDate,toDate';
        if (ref.includes('yearRequired')) return 'true';

        return '•';
    }

    public getFormData(): Record<string, any> {
        return this.form.getRawValue();
    }

    /**
     * Clears every registered control back to its initial empty value (select => '', text => undefined),
     * letting the user reconfigure from scratch without closing the dialog. Does not touch persisted state
     * until the user clicks Apply.
     */
    public resetForm(): void {
        if (!this.form) return;

        const resetValues: Record<string, any> = {};
        this.getAllNodes()
            .filter(node => this.isControlNode(node))
            .forEach(node => {
                resetValues[this.getControlKey(node)] = node.kind === 'select' ? '' : undefined;
            });

        this.form.reset(resetValues, { emitEvent: false });
        this.contextValues = {};
        this.applyConditionalRequiredValidators();
    }
}
